import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { bookingApi, Booking } from '../services/bookingService';
import { PageHeader, SectionCard, EmptyState, LoadingState, BookingStatusPill, useConfirm } from '../components/ui';
import { tokens } from '../theme';
import { countLabel, formatDayMonthShort, formatMoney, formatWeekdayDay } from '../utils/format';

const CLOSED_STATUSES = ['CANCELLED', 'COMPLETED', 'NO_SHOW'];

/** Ta sama regula co w API dla widoku „nadchodzace”: od dzisiaj i jeszcze aktywne. */
const isUpcoming = (b: Booking) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(b.date) >= today && !CLOSED_STATUSES.includes(b.status);
};

const BookingRow: React.FC<{ booking: Booking; upcoming: boolean; onCancel?: () => void }> = ({ booking: b, upcoming, onCancel }) => {
  const date = new Date(b.date);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) auto' },
        columnGap: 2,
        rowGap: 1.25,
        alignItems: 'center',
        px: { xs: 2, md: 2.5 },
        py: 2,
        borderTop: `1px solid ${tokens.line}`,
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 56,
          py: 0.75,
          borderRadius: '12px',
          textAlign: 'center',
          bgcolor: upcoming ? tokens.plumSoft : tokens.surfaceStrong,
          color: upcoming ? tokens.plum : tokens.inkSoft,
          alignSelf: 'start',
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 750, lineHeight: 1.2, color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
          {date.getDate()}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 650, color: 'inherit' }}>
          {formatDayMonthShort(date).split(' ')[1]}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', wordBreak: 'break-word' }}>{b.service.name}</Typography>
        <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft, '&::first-letter': { textTransform: 'uppercase' } }}>
          {formatWeekdayDay(date)} · <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{b.startTime}–{b.endTime}</Box>
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, wordBreak: 'break-word' }}>
          {b.salon.name} · {b.staff.firstName} {b.staff.lastName}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'row', sm: 'column' }}
        alignItems={{ xs: 'center', sm: 'flex-end' }}
        gap={{ xs: 1.25, sm: 0.75 }}
        flexWrap="wrap"
        sx={{ gridColumn: { xs: '2', sm: 'auto' } }}
      >
        <BookingStatusPill status={b.status} size="small" />
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formatMoney(b.totalPrice)}
        </Typography>
        {onCancel && (
          <Button size="small" variant="outlined" color="error" onClick={onCancel}>
            Anuluj wizytę
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export const MyBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<'upcoming' | 'all'>('upcoming');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await bookingApi.getMyBookings(view === 'upcoming');
      setBookings(data);
    } catch {
      setError('Nie udało się załadować wizyt. Odśwież stronę za chwilę.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [view]);

  const handleCancel = async (id: string) => {
    try {
      await bookingApi.updateStatus(id, 'CANCELLED');
      setToast('Wizyta została anulowana');
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Nie udało się anulować wizyty. Spróbuj ponownie albo zadzwoń do salonu.');
    }
  };

  const askCancel = async (b: Booking) => {
    const ok = await confirm({
      title: 'Anulować wizytę?',
      details: (
        <>
          <strong>{b.service.name}</strong>
          <Box component="span" sx={{ display: 'block', '&::first-letter': { textTransform: 'uppercase' } }}>
            {formatWeekdayDay(b.date)}, {b.startTime}
          </Box>
          <Box component="span" sx={{ display: 'block', color: tokens.muted }}>{b.salon.name}</Box>
        </>
      ),
      description: 'Termin zostanie zwolniony. Anulowania nie można cofnąć — nową wizytę trzeba zarezerwować od nowa.',
      confirmLabel: 'Anuluj wizytę',
      cancelLabel: 'Zostaw wizytę',
      tone: 'danger',
    });
    if (ok) handleCancel(b.id);
  };

  // Widok „minione” dostaje wszystkie wizyty z API — pokazujemy z nich tylko te, ktore juz sie odbyly
  // albo zostaly zamkniete, od najnowszych.
  const upcomingList = view === 'upcoming' ? bookings : [];
  const pastList = view === 'all'
    ? bookings.filter((b) => !isUpcoming(b)).reverse()
    : [];
  const list = view === 'upcoming' ? upcomingList : pastList;

  const description = view === 'upcoming'
    ? (loading || !upcomingList.length
      ? 'Zarezerwowane terminy, które jeszcze przed Tobą.'
      : `Masz ${countLabel(upcomingList.length, 'nadchodzącą wizytę', 'nadchodzące wizyty', 'nadchodzących wizyt')}.`)
    : 'Wizyty, które się odbyły, oraz anulowane rezerwacje.';

  return (
    <Box>
      <PageHeader
        title="Moje wizyty"
        description={description}
        actions={
          <Button variant="contained" startIcon={<StorefrontOutlinedIcon />} onClick={() => navigate('/salons')}>
            Zarezerwuj wizytę
          </Button>
        }
      >
        <Tabs
          value={view}
          onChange={(_, v) => { if (v && v !== view) { setLoading(true); setView(v); } }}
          sx={{ borderBottom: `1px solid ${tokens.line}` }}
        >
          <Tab value="upcoming" label="Nadchodzące" />
          <Tab value="all" label="Minione" />
        </Tabs>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingState variant="table" rows={3} />
      ) : list.length === 0 ? (
        <SectionCard>
          {view === 'upcoming' ? (
            <EmptyState
              icon={<EventAvailableOutlinedIcon />}
              title="Nie masz zaplanowanych wizyt"
              description="Znajdź salon i wybierz dogodny termin — wizyta pojawi się tutaj."
              actions={<Button variant="outlined" onClick={() => navigate('/salons')}>Znajdź salon</Button>}
            />
          ) : (
            <EmptyState
              icon={<HistoryOutlinedIcon />}
              title="Nie masz jeszcze minionych wizyt"
              description="Tutaj zobaczysz wizyty, które się odbyły, i anulowane rezerwacje."
              actions={<Button variant="outlined" onClick={() => { setLoading(true); setView('upcoming'); }}>Pokaż nadchodzące</Button>}
            />
          )}
        </SectionCard>
      ) : (
        <SectionCard
          title={view === 'upcoming' ? 'Nadchodzące wizyty' : 'Minione i anulowane'}
          subtitle={countLabel(list.length, 'wizyta', 'wizyty', 'wizyt')}
          padding="none"
        >
          {list.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              upcoming={view === 'upcoming'}
              onCancel={view === 'upcoming' && ['PENDING', 'CONFIRMED'].includes(b.status) ? () => askCancel(b) : undefined}
            />
          ))}
        </SectionCard>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};
